import { Children, cloneElement, createContext, isValidElement, useContext, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Box, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import AddToHomeScreenOutlinedIcon from '@mui/icons-material/AddToHomeScreenOutlined'
import ListOutlinedIcon from '@mui/icons-material/ListOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';

import { AppContext } from '/providers/app-context'

export const StudioSidebarContext = createContext({ collapsed: false })

const StudioSidebar = ({ sectionMenu, selectedTemplate: suppliedTemplate }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { templateId } = useParams()
  const { user, logoutUser, templates } = useContext(AppContext)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('studio-sidebar-collapsed') === 'true'
    } catch {
      return false
    }
  })
  const selectedTemplate = suppliedTemplate ?? templates.find(template => template.template_id === templateId)
  const selectedTemplateId = selectedTemplate?.template_id
  const canUseSelectedTemplate = selectedTemplateId && !selectedTemplate?.archived
  const path = location.pathname
  const isCurrentPage = page => page === 'templates'
    ? path === '/templates'
    : page === 'issue-passes'
      ? path === '/issue-passes' || path.includes('/issue-passes')
      : page === 'manage-passes'
        ? path === '/manage-passes' || (path.includes('/passes') && !path.includes('/notify-passes'))
        : page === 'notify-passes'
          ? path === '/notify-passes' || path.includes('/notify-passes')
        : path === `/${page}` || path.startsWith(`/${page}/`)

  const navigateToPassAction = (templatePath, fallbackPath) => {
    navigate(canUseSelectedTemplate ? `/templates/${selectedTemplateId}/${templatePath}` : fallbackPath)
  }

  const openExternalLink = url => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const toggleCollapsed = () => {
    setCollapsed(current => {
      const next = !current
      try {
        window.localStorage.setItem('studio-sidebar-collapsed', String(next))
      } catch {
        // Ignore local storage failures, such as private browsing restrictions.
      }
      return next
    })
  }

  return (
    <StudioSidebarContext.Provider value={{ collapsed }}>
      <Box
        component="nav"
        aria-label="Studio navigation"
        sx={{
          width: collapsed ? 64 : 275,
          height: '100%',
          minHeight: 'calc(100vh - 64px)',
          boxSizing: 'border-box',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: theme => `1px solid ${theme.palette.divider}`,
          py: 1,
          transition: 'width 150ms ease',
        }}
      >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', px: 1, mb: 1 }}>
        <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
          <IconButton onClick={toggleCollapsed} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'} size="small">
            {collapsed ? <ChevronRightOutlinedIcon /> : <ChevronLeftOutlinedIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <SidebarItem collapsed={collapsed} active={isCurrentPage('templates')} icon={<DashboardOutlinedIcon />} label="Templates" endIcon={sectionVisible(sectionMenu, path, 'templates') && <ExpandMoreOutlinedIcon />} onClick={() => navigate('/templates')} />
      {sectionMenu && sectionParent(path) === 'templates' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      <SidebarItem collapsed={collapsed} active={isCurrentPage('issue-passes')} icon={<AddToHomeScreenOutlinedIcon />} label="Create Passes" endIcon={sectionVisible(sectionMenu, path, 'issue-passes') && <ExpandMoreOutlinedIcon />} onClick={() => navigateToPassAction('issue-passes', '/issue-passes')} />
      {sectionMenu && sectionParent(path) === 'issue-passes' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      <SidebarItem collapsed={collapsed} active={isCurrentPage('manage-passes')} icon={<ListOutlinedIcon />} label="Manage Passes" endIcon={sectionVisible(sectionMenu, path, 'manage-passes') && <ExpandMoreOutlinedIcon />} onClick={() => navigateToPassAction('passes', '/manage-passes')} />
      {sectionMenu && sectionParent(path) === 'manage-passes' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      <SidebarItem collapsed={collapsed} active={isCurrentPage('notify-passes')} icon={<EmailOutlinedIcon />} label="Notify Passes" endIcon={sectionVisible(sectionMenu, path, 'notify-passes') && <ExpandMoreOutlinedIcon />} onClick={() => navigateToPassAction('notify-passes', '/notify-passes')} />
      {sectionMenu && sectionParent(path) === 'notify-passes' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      {user?.permissions.client_admin && <SidebarItem collapsed={collapsed} active={isCurrentPage('reports-and-statistics')} icon={<BarChartOutlinedIcon />} label="Reports & Statistics" endIcon={sectionVisible(sectionMenu, path, 'reports-and-statistics') && <ExpandMoreOutlinedIcon />} onClick={() => navigate('/reports-and-statistics')} />}
      {sectionMenu && sectionParent(path) === 'reports-and-statistics' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      <SidebarItem collapsed={collapsed} active={isCurrentPage('download-apps')} icon={<PhoneAndroidOutlinedIcon />} label="Download Scanner Apps" endIcon={sectionVisible(sectionMenu, path, 'download-apps') && <ExpandMoreOutlinedIcon />} onClick={() => navigate('/download-apps')} />
      {sectionMenu && sectionParent(path) === 'download-apps' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}

      <Divider sx={{ my: 1 }} />
      {user?.permissions.client_admin && <SidebarItem collapsed={collapsed} active={isCurrentPage('billing')} icon={<ReceiptLongOutlinedIcon />} label="Billing" endIcon={sectionVisible(sectionMenu, path, 'billing') && <ExpandMoreOutlinedIcon />} onClick={() => navigate('/billing')} />}
      {sectionMenu && sectionParent(path) === 'billing' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      <SidebarItem collapsed={collapsed} active={isCurrentPage('account-management')} icon={<ManageAccountsOutlinedIcon />} label="Account" endIcon={sectionVisible(sectionMenu, path, 'account-management') && <ExpandMoreOutlinedIcon />} onClick={() => navigate('/account-management')} />
      {sectionMenu && sectionParent(path) === 'account-management' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      <SidebarItem collapsed={collapsed} active={isCurrentPage('support')} icon={<SupportAgentOutlinedIcon />} label="Support" endIcon={sectionVisible(sectionMenu, path, 'support') && <ExpandMoreOutlinedIcon />} onClick={() => navigate('/support')} />
      {sectionMenu && sectionParent(path) === 'support' && <SectionMenu collapsed={collapsed}>{sectionMenu}</SectionMenu>}
      </Box>
      <Box sx={{ flexShrink: 0, pt: 1 }}>
        <Divider sx={{ mb: 1 }} />
        <SidebarItem collapsed={collapsed} icon={<LanguageOutlinedIcon />} label="Smartix website" onClick={() => openExternalLink('https://www.smartix.uk')} />
        <SidebarItem collapsed={collapsed} icon={<LocalLibraryOutlinedIcon />} label="Documentation" onClick={() => openExternalLink('https://docs.smartix.uk')} />
        <SidebarItem collapsed={collapsed} icon={<SubscriptionsOutlinedIcon />} label="Video guides" onClick={() => openExternalLink('https://youtube.com/playlist?list=PLTtyjclKrSgg&si=nitxR1Yx9iVi4p73')} />
        <SidebarItem collapsed={collapsed} error icon={<LogoutOutlinedIcon />} label="Logout" onClick={logoutUser} />
      </Box>
      </Box>
    </StudioSidebarContext.Provider>
  )
}

const SidebarItem = ({ collapsed, active = false, error = false, icon, label, endIcon, onClick }) => (
  <Tooltip
    title={collapsed ? label : ''}
    placement="right"
    slotProps={{ tooltip: { sx: theme => ({ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, color: theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.white }) } }}
  >
    <List disablePadding>
      <ListItemButton
        onClick={onClick}
        selected={active}
        sx={{
          py: 0.65,
          px: collapsed ? 1.5 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: error ? 'error.main' : active ? 'primary.main' : 'text.primary',
          '&.Mui-selected': { color: 'primary.main', backgroundColor: 'action.selected' },
          '&.Mui-selected:hover': { backgroundColor: 'action.hover' }
        }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, color: 'inherit' }}>{icon}</ListItemIcon>
        {!collapsed && <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />}
        {!collapsed && endIcon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{endIcon}</Box>}
      </ListItemButton>
    </List>
  </Tooltip>
)

const SectionMenu = ({ collapsed, children }) => (
  <Box
    sx={{
      ml: collapsed ? 0 : 2,
      '& .MuiButtonGroup-root': { display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'stretch' },
      '& .MuiButton-root': { justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 1 : 2, py: 0.65, border: 0, borderRadius: 0 },
      '& .MuiButtonGroup-grouped': { border: 0 }
    }}
  >
    {collapseSectionMenu(children, collapsed)}
  </Box>
)

export default StudioSidebar

const collapseSectionMenu = (element, collapsed) => {
  if (!isValidElement(element)) return element

  const children = Children.map(element.props.children, child => collapseSectionMenu(child, collapsed))
  const label = getMenuLabel(element.props.children)
  const icon = label && getMenuIcon(label)

  if (!element.props.onClick || !icon) {
    return cloneElement(element, children === undefined ? {} : { children })
  }

  return cloneElement(element, {
    title: collapsed ? label : undefined,
    'aria-label': label,
    startIcon: collapsed ? undefined : icon,
    children: collapsed ? icon : children
  })
}

const getMenuLabel = children => {
  if (typeof children === 'string') return children.trim()
  return Children.toArray(children).map(child => isValidElement(child) ? getMenuLabel(child.props.children) : '').join(' ').trim()
}

const getMenuIcon = label => {
  const value = label.toLowerCase()
  if (value.includes('detail') || value.includes('view')) return <OpenInNewIcon />
  if (value.includes('edit')) return <EditOutlinedIcon />
  if (value.includes('duplicate')) return <ContentCopyOutlinedIcon />
  if (value.includes('issue') || value.includes('create') || value.includes('new')) return <AddCircleOutlineOutlinedIcon />
  if (value.includes('pass')) return <ListOutlinedIcon />
  if (value.includes('notify') || value.includes('message')) return <EmailOutlinedIcon />
  if (value.includes('synchron')) return <SyncOutlinedIcon />
  if (value.includes('unarchive')) return <UnarchiveOutlinedIcon />
  if (value.includes('archive')) return <ArchiveOutlinedIcon />
  if (value.includes('company')) return <BusinessOutlinedIcon />
  if (value.includes('contact') || value.includes('user')) return <PeopleOutlinedIcon />
  if (value.includes('certificate')) return <VerifiedUserOutlinedIcon />
  if (value.includes('api')) return <KeyOutlinedIcon />
  if (value.includes('password')) return <LockOutlinedIcon />
  if (value.includes('delete') || value.includes('termination')) return <DeleteOutlineOutlinedIcon />
  if (value.includes('credit') || value.includes('payment')) return <CreditCardOutlinedIcon />
  if (value.includes('invoice') || value.includes('billing')) return <ReceiptLongOutlinedIcon />
  return <ListOutlinedIcon />
}

const sectionParent = path => {
  if (path === '/templates' || path.startsWith('/templates/')) {
    if (path.includes('/issue-passes')) return 'issue-passes'
    if (path.includes('/notify-passes')) return 'notify-passes'
    if (path.includes('/passes')) return 'manage-passes'
    return 'templates'
  }
  if (path === '/issue-passes' || path.startsWith('/issue-passes/')) return 'issue-passes'
  if (path === '/manage-passes' || path.startsWith('/manage-passes/')) return 'manage-passes'
  if (path === '/notify-passes' || path.startsWith('/notify-passes/')) return 'notify-passes'
  if (path === '/synchronise' || path.startsWith('/synchronise/')) return 'templates'
  if (path === '/reports-and-statistics' || path.startsWith('/reports-and-statistics/')) return 'reports-and-statistics'
  if (path === '/download-apps' || path.startsWith('/download-apps/')) return 'download-apps'
  if (path === '/billing' || path.startsWith('/billing/')) return 'billing'
  if (path === '/account-management' || path.startsWith('/account-management/')) return 'account-management'
  if (path === '/support' || path.startsWith('/support/')) return 'support'
  return null
}

const sectionVisible = (sectionMenu, path, parent) => Boolean(sectionMenu && sectionParent(path) === parent)
